import { useState } from 'react'
import { motion } from 'framer-motion'

const faqs = [
  {
    question: 'What prosthetic components do you supply?',
    answer:
      'Our catalog covers lower limb and upper limb components: hydraulic and mechanical knee joints, carbon fiber prosthetic feet, silicone and gel liners, shuttle locks, pyramid and tube adapters, myoelectric hands, silicone cosmetic prostheses, stump socks and socket lamination materials such as carbon fiber cloth.'
  },
  {
    question: 'Who do you sell to?',
    answer:
      'We supply distributors, prosthetic and orthotic clinics, rehabilitation centres and hospital procurement teams. Orders are handled as wholesale or OEM supply rather than single retail units.'
  },
  {
    question: 'Which markets do you export to?',
    answer:
      'Shipments regularly go to Africa, the Middle East and South America, along with other regions where demand for cost-effective prosthetic components is growing. We work with several international logistics partners to keep delivery times predictable.'
  },
  {
    question: 'Can you support OEM and mixed containers?',
    answer:
      'Yes. Product codes, packaging and documentation can be adapted for OEM orders, and mixed containers across categories are available so you can cover knees, feet, liners and adapters in a single shipment.'
  },
  {
    question: 'How do I choose the right knee joint or foot?',
    answer:
      'Selection depends on the amputation level, the user activity level and body weight. Send us the amputation level, activity level and target weight range and we will recommend a matching knee, foot and adapter combination from the current catalog.'
  },
  {
    question: 'How do I request a quote?',
    answer:
      'Send the product code and target quantity through the contact form on this page. We reply with current pricing, lead time and shipping options, and can provide a full catalog on request.'
  }
]

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer }
  }))
}

const Faq = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section id="faq" className="bg-white py-20">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-teal-700">
            Buying Guide
          </p>
          <h2 className="mt-4 text-4xl font-semibold leading-tight text-slate-950 md:text-5xl">
            Sourcing questions we get asked most.
          </h2>
        </motion.div>

        <div className="mx-auto mt-12 max-w-3xl divide-y divide-slate-200 border-y border-slate-200">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index
            return (
              <div key={faq.question}>
                <h3>
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-6 py-5 text-left"
                  >
                    <span className="text-lg font-medium text-slate-900">{faq.question}</span>
                    <span
                      aria-hidden="true"
                      className="flex-none text-2xl leading-none text-teal-700"
                    >
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>
                </h3>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <p className="pb-6 text-base leading-7 text-slate-600">{faq.answer}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default Faq
